import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Avatar,
    IconButton,
    Button,
    Typography,
    Box,
    CircularProgress,
    SvgIcon,
    Switch,
} from '@mui/material';
import {
    Close as CloseIcon,
    Google as GoogleIcon,
    CalendarTodayOutlined as CalendarTodayOutlinedIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { calendarApiV4 as api } from 'api';
import { getOrganization } from 'redux/slices/organizationSlice';
import { useSnackbar } from 'notistack';
import { blue, blueGrey, red } from '@mui/material/colors';

const providerStyle = (providerType) => {
    const type = providerType?.toLowerCase() || '';

    switch (type) {
        case 'microsoft':
            return {
                backgroundColor: blue[900],
                icon: (
                    <SvgIcon color="white">
                        <path d="M7.88 12.04q0 .45-.11.87-.1.41-.33.74-.22.33-.58.52-.37.2-.87.2t-.85-.2q-.35-.21-.57-.55-.22-.33-.33-.75-.1-.42-.1-.86t.1-.87q.1-.43.34-.76.22-.34.59-.54.36-.2.87-.2t.86.2q.35.21.57.55.22.34.31.77.1.43.1.88zM24 12v9.38q0 .46-.33.8-.33.32-.8.32H7.13q-.46 0-.8-.33-.32-.33-.32-.8V18H1q-.41 0-.7-.3-.3-.29-.3-.7V7q0-.41.3-.7Q.58 6 1 6h6.5V2.55q0-.44.3-.75.3-.3.75-.3h12.9q.44 0 .75.3.3.3.3.75V10.85l1.24.72h.01q.1.07.18.18.07.12.07.25zm-6-8.25v3h3v-3zm0 4.5v3h3v-3zm0 4.5v1.83l3.05-1.83zm-5.25-9v3h3.75v-3zm0 4.5v3h3.75v-3zm0 4.5v2.03l2.41 1.5 1.34-.8v-2.73zM9 3.75V6h2l.13.01.12.04v-2.3zM5.98 15.98q.9 0 1.6-.3.7-.32 1.19-.86.48-.55.73-1.28.25-.74.25-1.61 0-.83-.25-1.55-.24-.71-.71-1.24t-1.15-.83q-.68-.3-1.55-.3-.92 0-1.64.3-.71.3-1.2.85-.5.54-.75 1.3-.25.74-.25 1.63 0 .85.26 1.56.26.72.74 1.23.48.52 1.17.81.69.3 1.56.3zM7.5 21h12.39L12 16.08V17q0 .41-.3.7-.29.3-.7.3H7.5zm15-.13v-7.24l-5.9 3.54Z" />
                    </SvgIcon>
                ),
            };            
        case 'google':
            return {
                backgroundColor: red[500],
                icon: <GoogleIcon fontSize="small" />
            };
            
        default:
            // Icono genérico para cualquier otro proveedor
            return {
                backgroundColor: blueGrey[500],
                icon: <CalendarTodayOutlinedIcon fontSize="small" />
            };
    }
};

const ICalendarManagerDialog = (props) => {
    const { openDialog, onCloseDialog } = props;
    const [saving, setIsSaving] = useState(false);
    const { t } = useTranslation();
    const organization = useSelector(getOrganization);
    const { id: organizationId } = organization;

    const [providers, setProviders] = useState([]);

    const { enqueueSnackbar } = useSnackbar();
    
    const loadData = useCallback(() => {
        api.get('providers/list-providers', { params: { organizationId } })
        .then((response) => {
            setProviders(response.data);
        })
        .catch((error) => {
            console.error(error);
        });
    }, [organizationId]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleChangeActive = useCallback(async (provider) => {
        const { id, active } = provider;
        setIsSaving(true);

        const previousProviders = providers;
        setProviders(prevProviders =>
            prevProviders.map(p =>
            p.id === id ? { ...p, active: !p.active } : p
            )
        );
        try {
            await api.post(`providers/${active ? 'detach-organization' : 'attach-organization'}`, {
                providerId: id,
                organizationId
            });

            enqueueSnackbar(t('common.term.saveSuccess'), { variant: 'success' });
        } catch (error) {
            setProviders(previousProviders);
            enqueueSnackbar(t('profile.plans.selectAddOns.failSave'), { variant: 'error' });
        } finally {
            setIsSaving(false);
        }
    }, [enqueueSnackbar, organizationId, providers, t]);

    return (
        <Dialog
            open={openDialog}
            onClose={onCloseDialog}
            fullWidth
            maxWidth="sm"
            PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
        >
            <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight="bold">
                    {t('profile.settings.calendar.providers')}
                </Typography>
                <IconButton onClick={onCloseDialog} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ borderBottom: 'none' }}>
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            {t('profile.settings.calendar.linkedProviders')}
                        </Typography>
                    </Box>

                    <List>
                        {providers.map((item) => {
                            const { backgroundColor, icon } = providerStyle(item.type);
                            return (
                                <ListItem
                                    key={item.id}
                                    sx={{
                                        mb: 1,
                                        bgcolor: 'action.hover',
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                    secondaryAction={
                                        saving ? ( <CircularProgress size={20} /> ) : (
                                            <Switch
                                                checked={item.active}
                                                onChange={(e) => handleChangeActive(item)}
                                                color={'primary'}
                                            />
                                        )
                                    }
                                >
                                    <ListItemAvatar>
                                        <Avatar sx={{ bgcolor: backgroundColor, color: 'white' }}>
                                            {
                                                icon
                                            }
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={item.type.toUpperCase()}
                                        primaryTypographyProps={{ fontWeight: 'medium' }}
                                    />
                                </ListItem>
                            )
                        })}
                    </List>
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2, justifyContent: 'flex-end' }}>
                <Button
                    disableElevation
                    onClick={onCloseDialog}
                    sx={{ borderRadius: 2, px: 3 }}
                    variant="outlined"
                >
                    {t('common.term.Cancel')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ICalendarManagerDialog;
